import _extends from "@babel/runtime/helpers/extends";
import { packagePickerErrorSrc17Evid2236 } from '@wix/bi-logger-premium-data/v2';
import { Box } from '@wix/design-system';
import { ErrorsUtils } from '@wix/package-picker-shared';
import { usePremiumEssentialsContext } from '@wix/premium-fed-sdk/@premium-essentials';
import * as Errors from '@wix/wix-ui-premium-purchase/@errors';
import React, { useEffect, useRef } from 'react';
import { useCurrentModule } from '../../components/ModuleProvider';
import { useBiLogger, useEclipsePanorama, getStaticBiEventsFields } from '../../hooks';
const extractRequestId = error => {
  var _response;
  return typeof error === 'object' && !!error ? error == null || (_response = error.response) == null ? void 0 : _response.requestId : undefined;
};
const ErrorPageWrapper = _ref => {
  let {
    children,
    ...props
  } = _ref;
  return /*#__PURE__*/React.createElement(Box, {
    height: "80vh"
  }, /*#__PURE__*/React.createElement(Box, _extends({
    dataHook: "app-error-page",
    width: "100%",
    height: "100%",
    align: "center",
    verticalAlign: "middle"
  }, props), children));
};
const GeneralErrorMapper = _ref2 => {
  let {
    error: generalError,
    ...uiProps
  } = _ref2;
  const {
    biLogger
  } = useBiLogger();
  const staticBiEventsFields = getStaticBiEventsFields();
  const module = useCurrentModule();
  const panorama = useEclipsePanorama();
  const errorType = ErrorsUtils.getErrorType(generalError);
  const reportedErrorsRef = useRef(new Set());
  const errorMessage = generalError instanceof Error ? generalError == null ? void 0 : generalError.message : undefined;
  const errorKey = `${errorType}-${errorMessage}`;
  const requestId = extractRequestId(generalError);
  useEffect(() => {
    if (!reportedErrorsRef.current.has(errorKey)) {
      reportedErrorsRef.current.add(errorKey);
      if (module.internals.bi.onError) {
        module.internals.bi.onError({
          errorType,
          errorMessage,
          requestId
        });
      } else {
        biLogger == null || biLogger.report(packagePickerErrorSrc17Evid2236({
          ...staticBiEventsFields,
          errorType,
          errorMessage,
          requestId
        }));
      }
      panorama.reportError(new Error(errorMessage), {
        tags: {
          errorType,
          errorMessage: errorMessage == null ? void 0 : errorMessage.replace('\n', ' '),
          requestId
        }
      });
    }
  }, [errorKey, errorType, errorMessage, requestId, module.internals.bi, biLogger, staticBiEventsFields, panorama]);
  const Component = React.useMemo(() => {
    return () => {
      const ErrorComponent = Errors[errorType];
      return /*#__PURE__*/React.createElement(ErrorComponent, uiProps[errorType]);
    };
  }, [errorType, uiProps]);
  return /*#__PURE__*/React.createElement(Component, null);
};
const CustomerCareLink = _ref3 => {
  let {
    children
  } = _ref3;
  return /*#__PURE__*/React.createElement("a", {
    href: "https://www.wix.com/contact",
    target: "_blank",
    rel: "noreferrer"
  }, children);
};
export const AppErrorsMapper = _ref4 => {
  let {
    error
  } = _ref4;
  const module = useCurrentModule();
  const {
    siteId
  } = usePremiumEssentialsContext();
  const {
    t
  } = module.internals.useTranslations();
  const technicalErrorSubtitle = /*#__PURE__*/React.createElement(React.Fragment, null, t('premium-purchase-plan.eclipse-package-picker.technical-error.body1'), /*#__PURE__*/React.createElement("br", null), /*#__PURE__*/React.createElement(CustomerCareLink, null, t('premium-purchase-plan.eclipse-package-picker.technical-error.body2')));
  const networkErrorSubtitle = /*#__PURE__*/React.createElement(React.Fragment, null, t('premium-purchase-plan.eclipse-package-picker.network-error.body1'), /*#__PURE__*/React.createElement("br", null), /*#__PURE__*/React.createElement(CustomerCareLink, null, t('premium-purchase-plan.eclipse-package-picker.network-error.body2')));
  return /*#__PURE__*/React.createElement(ErrorPageWrapper, null, /*#__PURE__*/React.createElement(GeneralErrorMapper, {
    error: error,
    TechnicalError: {
      content: {
        title: t('premium-purchase-plan.eclipse-package-picker.technical-error.title'),
        subtitle: technicalErrorSubtitle,
        refresh: t('premium-purchase-plan.eclipse-package-picker.technical-error.cta')
      }
    },
    ConnectionError: {
      content: {
        title: t('premium-purchase-plan.eclipse-package-picker.network-error.title'),
        subtitle: networkErrorSubtitle,
        tryAgain: t('premium-purchase-plan.eclipse-package-picker.network-error.cta')
      }
    },
    PermissionErrorWrapper: {
      contentKeys: {
        title: t('premium-purchase-plan.studio-package-picker.permissions-error.title'),
        subtitle: t('premium-purchase-plan.studio-package-picker.permissions-error.body'),
        subtitleNoSiteName: t('premium-purchase-plan.studio-package-picker.permissions-error.body'),
        goToDashboard: t('premium-purchase-plan.studio-package-picker.permissions-error.cta')
      },
      msid: siteId,
      t,
      onActionClick: () => {
        location.reload();
      }
    }
  }));
};
//# sourceMappingURL=AppErrorsMapper.js.map